import { useCallback } from 'react'
import { useCalendarClient } from '@frontend/clients/calendar.request'
import { useShipmentsClient } from '@frontend/clients/shipments.request'
import { useDebounce } from '@frontend/components/hooks'
import { SPOListFilterParamExtended } from '@frontend/components/ShipmentList/SPOListTable'
import { getCalendarBoundaries } from '@frontend/lib/calendar'
import CalendarBookingStore from '@frontend/stores/CalendarBookingStore'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import {
  UIListShipmentAggregatedQueryFilter,
  UIListShipmentSummariesQueryFilter,
} from '@shared-types/shipments'
import { ShipmentAuditEventType } from '@shared-types/shipments/audit_event'
import { Identifier, NanoId, Nullable } from '@shared-types/util'

import { fetchAllPages } from './utils'

export function useQueryShipment(nanoId?: NanoId) {
  const shipmentClient = useShipmentsClient()

  return useQuery({
    queryKey: ['shipment_details', nanoId],

    queryFn: () => {
      if (!nanoId) {
        return null
      }

      return shipmentClient.getDetails(nanoId)
    },
    staleTime: Infinity,
  })
}

export function useQueryAllShipmentsSummaries(
  shipmentSummaryParams: UIListShipmentSummariesQueryFilter,
  options?: { refetchInterval?: number }
) {
  const shipmentsClient = useShipmentsClient()
  const debouncedParams = useDebounce(shipmentSummaryParams)

  return useQuery({
    queryKey: ['shipment_summaries_list', debouncedParams],

    queryFn: () => shipmentsClient.listShipmentSummaries(debouncedParams),
    refetchInterval: options?.refetchInterval,
  })
}

export function useQueryAllYardManagementShipments(
  shipmentSummaryParams: UIListShipmentSummariesQueryFilter,
  options?: { refetchInterval?: number }
) {
  const shipmentsClient = useShipmentsClient()
  const debouncedParams = useDebounce(shipmentSummaryParams)

  return useQuery({
    queryKey: ['yard_shipment_summaries_list', debouncedParams],

    queryFn: () =>
      shipmentsClient.listShipmentSummaries({
        ...debouncedParams,
        current_time: new Date().toISOString(),
      }),
    refetchInterval: options?.refetchInterval,
  })
}

export function useQueryAllShipmentsSummariesWithTimestampFilter(
  shipmentSummaryParams: UIListShipmentSummariesQueryFilter
) {
  const shipmentsClient = useShipmentsClient()
  const debouncedParams = useDebounce(shipmentSummaryParams)

  return useQuery({
    queryKey: ['shipment_summaries_list', debouncedParams],

    queryFn: async () => {
      const noParamsSet = [
        debouncedParams.checked_in_at__date,
        debouncedParams.truck_arrived_at__date,
        debouncedParams.unload_completed_at__date,
        debouncedParams.rejected_at__date,
        debouncedParams.exited_at__date,
        debouncedParams.q_owner,
        debouncedParams.q_counterpart,
      ].every((param) => !param)

      if (noParamsSet) {
        return []
      }

      const results = await fetchAllPages(debouncedParams, (filters) =>
        shipmentsClient.listShipmentSummaries(filters)
      )
      return results.results
    },
  })
}

export function useQueryAllShipmentPurchaseOrderSummaries(filters: SPOListFilterParamExtended) {
  const shipmentsClient = useShipmentsClient()
  const debouncedFilters = useDebounce(filters)
  return useQuery({
    queryKey: ['spo_summaries_list', debouncedFilters],

    queryFn: () => shipmentsClient.listSPOSummaries(debouncedFilters),
  })
}

export function useQuerySPOsAggregated(filters: SPOListFilterParamExtended) {
  const shipmentsClient = useShipmentsClient()
  const debouncedFilters = useDebounce(filters)
  return useQuery({
    queryKey: ['spos_aggregated', debouncedFilters],

    queryFn: () => shipmentsClient.sposAggregated(debouncedFilters),
  })
}

export function useClearShipmentSummariesCache() {
  const queryClient = useQueryClient()
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['shipment_summaries_aggregated'] }),
      queryClient.invalidateQueries({ queryKey: ['shipment_summaries_list'] }),
      queryClient.invalidateQueries({ queryKey: ['yard_shipment_summaries_list'] }),
      queryClient.invalidateQueries({ queryKey: ['spo_summaries_list'] }),
      queryClient.invalidateQueries({ queryKey: ['spos_aggregated'] }),
    ])
  }, [queryClient])
}

export function useClearAllShipmentDetailsCache() {
  const queryClient = useQueryClient()
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['shipment_details'] }),
      queryClient.invalidateQueries({ queryKey: ['shipment_audit_events'] }),
    ])
  }, [queryClient])
}

export function useRefetchShipmentDetails() {
  const queryClient = useQueryClient()
  return useCallback(
    async (nanoId: NanoId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['shipment_details', nanoId] }),
        queryClient.invalidateQueries({ queryKey: ['shipment_audit_events', nanoId] }),
      ])
    },
    [queryClient]
  )
}

export function useQueryFacilityUnscheduledShipments(
  facilityId: Nullable<Identifier>,
  nid__search: string
) {
  const calendarClient = useCalendarClient()
  const debouncedNid = useDebounce(nid__search)

  return useQuery({
    queryKey: ['shipments_without_bookings', facilityId, debouncedNid],

    queryFn: async () => {
      if (!facilityId || debouncedNid.length < 2) {
        return []
      }
      const { results } = await calendarClient.searchUnscheduledShipments(facilityId, debouncedNid)
      return results
    },
  })
}

export function useQueryListShipmentAuditEvents(
  nid: Nullable<NanoId>,
  filters: ShipmentAuditEventType[]
) {
  const shipmentsClient = useShipmentsClient()

  return useQuery({
    queryKey: ['shipment_audit_events', nid, filters],

    queryFn: async () => {
      if (!nid) {
        return []
      }

      return shipmentsClient.listShipmentAuditEvents(nid, filters)
    },

    refetchOnMount: true,
  })
}

type QueryDeliveredSummariesParams = {
  facilityId: Nullable<Identifier>
  refetchInterval?: number
}

export function useQueryDeliveredSummaries({
  facilityId,
  refetchInterval,
}: QueryDeliveredSummariesParams) {
  const calendarClient = useCalendarClient()
  const selectedMonth = CalendarBookingStore.useState((s) => s.selectedMonth)
  const { start_date, end_date } = getCalendarBoundaries(selectedMonth)
  const debouncedFilters = useDebounce({ start_date, end_date })
  // start_date/end_date diverge from debouncedFilters while the debounce timer is pending, e.g.
  // right after switching months, before the query itself has re-run for the new range
  const isDebouncingFilters =
    JSON.stringify({ start_date, end_date }) !== JSON.stringify(debouncedFilters)

  const query = useQuery({
    queryKey: ['facility_shipment_delivered_summaries', facilityId, debouncedFilters],

    queryFn: async () => {
      if (!facilityId) {
        return null
      }
      return calendarClient.getDeliveredShipmentSummaries(facilityId, {
        start_date: debouncedFilters.start_date,
        end_date: debouncedFilters.end_date,
      })
    },

    refetchInterval,
  })

  return { ...query, isPending: query.isPending || isDebouncingFilters }
}

type ShipmentPurchaseOrderDayViewFilters = {
  q?: string
  shipment__booking_date: string
  shipment__receiving_company_id?: string
  limit: number
  sort_by?: string | null | undefined
}

export function useQueryShipmentPurchaseOrderDayView(filters: ShipmentPurchaseOrderDayViewFilters) {
  const shipmentsClient = useShipmentsClient()
  const debouncedFilters = useDebounce(filters)
  return useQuery({
    queryKey: ['spo_summaries_list', debouncedFilters],

    queryFn: async () => {
      if (!debouncedFilters.shipment__receiving_company_id) {
        return { results: [] }
      }

      return shipmentsClient.listSPOSummaries(debouncedFilters)
    },
  })
}

export function useQueryShipmentSummariesAggregated(filters: UIListShipmentAggregatedQueryFilter) {
  const shipmentsClient = useShipmentsClient()
  const debouncedFilters = useDebounce(filters)
  return useQuery({
    queryKey: ['shipment_summaries_aggregated', debouncedFilters],

    queryFn: async () => {
      return shipmentsClient.listShipmentSummariesAggregated(debouncedFilters)
    },
  })
}
